import DashboardLayout from "../../../components/DashboardLayout";

import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { IState } from "../../../types";
import { EditorContainer, InnerEditorContainer } from "./styled";
import useVideo from "../../../hooks/useVideo";
import useFetchVideoEditorFonts from "../../../hooks/useFetchVideoEditorFonts";
import { SettingsProvider } from "./studio/context";
import MainView from "./studio";
import {
  TimelineProvider,
  TimelineInitialState,
} from "./studio/remotion-timeline/context/provider";
import { clearActiveVideo } from "../../../redux/slices/transcript";
import { useAppDispatch } from "../../../redux/hooks";

const initialState: TimelineInitialState = {
  tracks: [],
  fps: 30,
};

const isMobileViewport = () => {
  if (typeof window !== "undefined") {
    return window.innerWidth <= 768; // Adjust this breakpoint as needed
  }
  return false;
};

const Editor = () => {
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const [firstLoadData, setFirstLoadData] = useState<boolean>(false);
  const user = useSelector((state: IState) => state.user.details);
  const selectedWorkspace = useSelector(
    (state: IState) => state.user.selectedWorkspace
  );

  const { fetchVideoEditorFonts, fonts } = useFetchVideoEditorFonts();
  const { getVideo, video } = useVideo();

  const originalBodyOverflow = useRef<string | null>(null); // For the general 'overflow' property
  const originalBodyTouchAction = useRef<string | null>(null);

  const updateFirstLoad = () => {
    setFirstLoadData(true);
  };

  useEffect(() => {
    const applyScrollLock = () => {
      if (isMobileViewport()) {
        if (document.body) {
          originalBodyOverflow.current = document.body.style.overflow;
          originalBodyTouchAction.current = document.body.style.touchAction;

          document.body.style.overflow = "hidden";

          document.body.style.touchAction = "none";
        }
      }
    };

    const removeScrollLock = () => {
      if (document.body) {
        if (originalBodyOverflow.current !== null) {
          document.body.style.overflow = originalBodyOverflow.current;
        }
        if (originalBodyTouchAction.current !== null) {
          document.body.style.touchAction = originalBodyTouchAction.current;
        }
      }
    };

    applyScrollLock();

    return () => {
      removeScrollLock();
    };
  }, []);

  useEffect(() => {
    return () => {
      dispatch(clearActiveVideo());
    };
  }, [dispatch]);

  useEffect(() => {
    if (id) {
      setFirstLoadData(false);
      dispatch(clearActiveVideo());
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (selectedWorkspace?.id && user?.uid && id && !firstLoadData) {
      getVideo(id, updateFirstLoad);
      if (fonts?.length === 0) {
        fetchVideoEditorFonts();
      }
    }
  }, [
    firstLoadData,
    getVideo,
    id,
    selectedWorkspace?.id,
    video.id,
    user?.uid,
    fonts?.length,
    fetchVideoEditorFonts,
  ]);

  return (
    <DashboardLayout showNavigation={false}>
      <EditorContainer>
        <InnerEditorContainer>
          <TimelineProvider initialState={initialState}>
            <SettingsProvider fonts={fonts}>
              <MainView />
            </SettingsProvider>
          </TimelineProvider>
        </InnerEditorContainer>
      </EditorContainer>
    </DashboardLayout>
  );
};

export default Editor;
